import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PAGE_PARAM } from '@api';
import { FilterOption } from '@common/organisms/CommonFilters/GenericFilterMultiSelect';
import ListFilters from '@common/ui/ListFilters/ListFilters';

import AssessmentReportResourcesFilter from './AssessmentReportResourcesFilter';
import AssessmentReportPermissionsFilter from './AssessmentReportPermissionsFilter';
import AssessmentReportRiskScoreFilter from './AssessmentReportRiskScoreFilter';
import {
  ASSESSMENT_REPORT_IN_ACCESS_FLOW_FILTER,
  ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER,
  ASSESSMENT_REPORT_PERMISSIONS_FILTER,
  ASSESSMENT_REPORT_RESOURCES_FILTER,
  ASSESSMENT_REPORT_REVOKED_FILTER,
  ASSESSMENT_REPORT_RISK_SCORE_FILTER,
  ASSESSMENT_REPORT_TIER_FILTER,
} from '@StandingAccess/types';
import AssessmentReportBooleanFilter from './AssessmentReportBooleanFilter';
import AssessmentReportLinkedElementsFilter from './AssessmentReportLinkedElementsFilter';
import AssessmentReportTierFilter from './AssessmentReportTierFilter';

export interface AssessmentReportFiltersOptions {
  status: FilterOption[];
  tag: FilterOption[];
  resourceType: FilterOption[];
}

interface AssessmentReportFiltersProps {
  reportId: string;
  allowedFilters?: string[];
}

export default function AssessmentReportFilters({ reportId, allowedFilters }: AssessmentReportFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const onSelected = useCallback(
    (value: string[], param: string) => {
      searchParams.delete(PAGE_PARAM);
      searchParams.delete(param);
      value.forEach((v) => searchParams.append(param, v));
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const filters = useMemo(
    () =>
      [
        {
          name: ASSESSMENT_REPORT_TIER_FILTER.label,
          queryParam: ASSESSMENT_REPORT_TIER_FILTER.param,
          element: (
            <AssessmentReportTierFilter
              selected={searchParams.getAll(ASSESSMENT_REPORT_TIER_FILTER.param) || []}
              onSelect={(value) => onSelected(value, ASSESSMENT_REPORT_TIER_FILTER.param)}
            />
          ),
        },
        {
          name: ASSESSMENT_REPORT_RESOURCES_FILTER.label,
          queryParam: ASSESSMENT_REPORT_RESOURCES_FILTER.param,
          element: (
            <AssessmentReportResourcesFilter
              reportId={reportId}
              selected={searchParams.getAll(ASSESSMENT_REPORT_RESOURCES_FILTER.param) || []}
              onSelect={(value) => onSelected(value, ASSESSMENT_REPORT_RESOURCES_FILTER.param)}
            />
          ),
        },
        {
          name: ASSESSMENT_REPORT_PERMISSIONS_FILTER.label,
          queryParam: ASSESSMENT_REPORT_PERMISSIONS_FILTER.param,
          element: (
            <AssessmentReportPermissionsFilter
              reportId={reportId}
              selected={searchParams.getAll(ASSESSMENT_REPORT_PERMISSIONS_FILTER.param) || []}
              onSelect={(value) => onSelected(value, ASSESSMENT_REPORT_PERMISSIONS_FILTER.param)}
            />
          ),
        },
        {
          name: ASSESSMENT_REPORT_RISK_SCORE_FILTER.label,
          queryParam: ASSESSMENT_REPORT_RISK_SCORE_FILTER.param,
          element: (
            <AssessmentReportRiskScoreFilter
              reportId={reportId}
              selected={searchParams.getAll(ASSESSMENT_REPORT_RISK_SCORE_FILTER.param) || []}
              onSelect={(value) => onSelected(value, ASSESSMENT_REPORT_RISK_SCORE_FILTER.param)}
            />
          ),
        },
        {
          name: ASSESSMENT_REPORT_IN_ACCESS_FLOW_FILTER.label,
          queryParam: ASSESSMENT_REPORT_IN_ACCESS_FLOW_FILTER.param,
          element: (
            <AssessmentReportBooleanFilter
              label={ASSESSMENT_REPORT_IN_ACCESS_FLOW_FILTER.label}
              selected={searchParams.getAll(ASSESSMENT_REPORT_IN_ACCESS_FLOW_FILTER.param) || []}
              onSelect={(value) => onSelected(value, ASSESSMENT_REPORT_IN_ACCESS_FLOW_FILTER.param)}
            />
          ),
        },
        {
          name: ASSESSMENT_REPORT_REVOKED_FILTER.label,
          queryParam: ASSESSMENT_REPORT_REVOKED_FILTER.param,
          element: (
            <AssessmentReportBooleanFilter
              label={ASSESSMENT_REPORT_REVOKED_FILTER.label}
              selected={searchParams.getAll(ASSESSMENT_REPORT_REVOKED_FILTER.param) || []}
              onSelect={(value) => onSelected(value, ASSESSMENT_REPORT_REVOKED_FILTER.param)}
            />
          ),
        },
        {
          name: ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER.label,
          queryParam: ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER.param,
          element: (
            <AssessmentReportLinkedElementsFilter
              label={ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER.label}
              reportId={reportId}
              selected={searchParams.getAll(ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER.param) || []}
              filter={{
                revoked: false,
              }}
              onSelect={(value) => onSelected(value, ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER.param)}
            />
          ),
        },
      ].filter((filter) => allowedFilters?.includes(filter.queryParam)),
    [reportId, searchParams, onSelected, allowedFilters],
  );

  const hasActiveFilters = useMemo(() => {
    for (const filter of filters) {
      if (searchParams.has(filter.queryParam)) {
        return true;
      }
    }
    return false;
  }, [filters, searchParams]);

  const handleOnClear = useCallback(() => {
    searchParams.delete(PAGE_PARAM);
    for (const filter of filters) {
      searchParams.delete(filter.queryParam);
    }
    setSearchParams(searchParams);
  }, [filters, searchParams, setSearchParams]);

  return (
    <ListFilters
      visibleFilters={allowedFilters || []}
      filters={filters}
      onClear={hasActiveFilters ? handleOnClear : undefined}
    />
  );
}
